import type { SlackLinkNonceDisplayRecord } from "@/shared/domain/slack";
import { Button } from "@/shared/foundation/primitives";

import styles from "./LinkSlack.module.css";

export interface LinkSlackCardProps {
  pending: SlackLinkNonceDisplayRecord;
  onConfirm: () => void;
  isConfirming: boolean;
  errorMessage?: string;
  onCancel: () => void;
}

/**
 * The confirmation step of the Slack account link.
 *
 * Names the Slack account before offering the button, because the risk here runs the
 * counter-intuitive way: the danger is not someone stealing your link, it is being handed
 * someone else's. Confirming that binds *their* Slack account to your Roboto account, letting
 * them ask Roboto questions as you. A reader who does not recognize the account should stop.
 */
export function LinkSlackCard({
  pending,
  onConfirm,
  isConfirming,
  errorMessage,
  onCancel,
}: LinkSlackCardProps) {
  return (
    <div className={styles.card}>
      <h1 className={styles.heading}>Link your Slack account</h1>

      <div className={styles.account}>
        <span className={styles["account-name"]}>
          {pending.slack_user_display}
        </span>
        {pending.team_name !== null && (
          <span className={styles["account-workspace"]}>
            {pending.team_name}
          </span>
        )}
      </div>

      <p className={styles.body}>
        Confirming lets this Slack account talk to Roboto as you. Only continue
        if it is yours.
      </p>

      {errorMessage !== undefined && (
        <p className={styles.error} role="alert">
          {errorMessage}
        </p>
      )}

      <div className={styles.actions}>
        <Button variant="text" onClick={onCancel} disabled={isConfirming}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirm} loading={isConfirming}>
          Link account
        </Button>
      </div>
    </div>
  );
}
